'use client';

import { useEffect } from 'react';
import { useShellRouter } from '@/hooks/useShellRouter';
import { saveLastVisitedRoute } from '../utils/lastVisitedRoute';
import { posthog } from '../lib/posthog';

/**
 * Convert detail/child routes to their parent route
 * e.g., '/fuel-oil-detail' -> '/fuel-oil-analysis' (or appropriate parent)
 *       '/forms-detail' -> '/forms'
 *       '/cii-detail' -> '/cii'
 */
function getParentRoute(pathname: string): string {
  // Map of detail routes to their parent routes
  const detailToParentMap: Record<string, string> = {
    '/fuel-oil-detail': '/fuel-oil-analysis',
    '/forms-detail': '/forms',
    '/eeoi-detail': '/eeoi',
    '/cii-detail': '/cii',
  };

  // Check if this is a known detail route
  if (detailToParentMap[pathname]) {
    return detailToParentMap[pathname];
  }

  // Generic fallback: if route ends with '-detail', try removing it
  if (pathname.endsWith('-detail')) {
    return pathname.replace('-detail', '');
  }

  // Return original route if no transformation needed
  return pathname;
}

/**
 * Component to track route changes and save last visited route
 * This ensures when opening in a new tab, the user returns to their last visited page
 * Detail/child routes are converted to parent routes for proper marketplace matching
 */
export default function RouteTracker() {
  const router = useShellRouter();

  useEffect(() => {
    // Save route on initial load (but not the index page)
    if (router.pathname !== '/' && router.pathname !== '/index') {
      const parentRoute = getParentRoute(router.pathname);
      saveLastVisitedRoute(parentRoute);
    }

    if (posthog.__loaded) {
      posthog.capture('$pageview');
    }

    // Listen for route changes
    const handleRouteChange = (url: string) => {
      // Extract pathname from URL (remove query params)
      const pathname = url.split('?')[0];
      // Don't save index page as last visited
      if (pathname !== '/' && pathname !== '/index') {
        const parentRoute = getParentRoute(pathname);
        saveLastVisitedRoute(parentRoute);
      }

      if (posthog.__loaded) {
        posthog.capture('$pageview', { $current_url: window.location.origin + url });
      }
    };

    router.events.on('routeChangeComplete', handleRouteChange);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router]);

  return null;
}
